/* One list for every DocType.
 *
 * The list is where somebody who does not know the system finds out what is
 * in it, so the work here is mostly about making a table of unfamiliar
 * records legible: the columns the DocType itself says matter, money on the
 * right, status as a shape and not only a colour, and a sidebar of counts
 * that turns "2,431 invoices" into somewhere to start.
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  EmptyByFilter, EmptyFirstTime, Failed, Loading, NoAccess, Stale, TooMuchData, useToast,
} from '@jackdesk/shell'
import type { ErpError } from './api'
import { useErp } from './context'
import {
  DOCSTATUS_LABEL, docstatusChip, formatCurrency, formatDate, formatDatetime,
  formatNumber, formatSince,
} from './format'
import type { Doc, DocField, DocMeta } from './types'
import { readColumns, readViews, removeView, saveView, writeColumns } from './views'
import { GanttView, ganttConfig, ganttFields } from './GanttView'
import { MapView, canMap, mapFields } from './MapView'
import { CalendarView, KanbanView, boardField, calendarField } from './BoardView'
import type { SavedView } from './views'

//: Beyond this, a list is not something anybody reads -- it is something they
//: should have filtered. Said out loud rather than just paginated silently.
const TOO_MANY = 2000

//: How long a list can sit open before it admits it might be out of date.
const STALE_AFTER_MINUTES = 10

const PAGE = 50

type Props = {
  doctype: string
  onOpen: (name: string) => void
  onNew?: () => void
  /** Open the importer for this DocType. */
  onImport?: () => void
  /** What the menu calls it. "Chart of accounts" reads better than
   *  "Account", and somebody who clicked that menu item should land on a
   *  page with the same words on it. */
  title?: string
  /** Anything the screen wants beside the New button. */
  actions?: React.ReactNode
}

export function ListView({ doctype, onOpen, onNew, onImport, title, actions }: Props) {
  const erp = useErp()
  const [meta, setMeta] = useState<DocMeta | null>(null)
  const [rows, setRows] = useState<Doc[]>([])
  const [total, setTotal] = useState<number | undefined>()
  const [hasMore, setHasMore] = useState(false)
  const [start, setStart] = useState(0)
  const [search, setSearch] = useState('')
  const [quick, setQuick] = useState<Record<string, string>>({})
  const [groupField, setGroupField] = useState<string | null>(null)
  const [counts, setCounts] = useState<{ name: string; count: number }[]>([])
  const [sort, setSort] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<ErpError | null>(null)
  const [downloading, setDownloading] = useState<'csv' | 'xlsx' | null>(null)
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [chosenColumns, setChosenColumns] = useState<string[] | null>(null)
  const [views, setViews] = useState<SavedView[]>([])
  const [busy, setBusy] = useState(false)
  const [totalling, setTotalling] = useState(false)
  const [shape, setShape] = useState<'table' | 'board' | 'calendar' | 'gantt' | 'map'>('table')
  /** The combined first request already drew page one; do not fetch it twice. */
  const firstPage = useRef(false)
  const searchBox = useRef<HTMLInputElement>(null)
  /** When this list was last read, so it can admit to being out of date. */
  const readAt = useRef(Date.now())
  const [staleSince, setStaleSince] = useState<string | null>(null)
  const toast = useToast()
  const live = useRef(0)

  useEffect(() => {
    let cancelled = false
    setMeta(null); setRows([]); setStart(0); setQuick({}); setSearch(''); setLoading(true)
    setPicked(new Set())
    const saved = readColumns(doctype)
    setChosenColumns(saved)
    setViews(readViews(doctype))
    // One request for the metadata *and* the first page. The server knows the
    // metadata already, so it can pick the columns when nobody has chosen any
    // — which is what turned three serial round trips into two.
    const forGantt = ganttConfig(doctype)
    erp.listScreen(doctype, {
      fields: saved ?? undefined,
      extra: forGantt ? ganttFields(forGantt) : undefined,
      limit: PAGE,
    })
      .then(({ meta: m, list }) => {
        if (cancelled) return
        setMeta(m)
        setSort(`${m.sort_field || 'modified'} ${m.sort_order || 'desc'}`)
        if (!m.no_access) {
          setRows(list.rows); setHasMore(list.has_more); setTotal(list.total)
          firstPage.current = true
          setLoading(false)
        }
      })
      .catch((e) => { if (!cancelled) { setError(e as ErpError); setLoading(false) } })
    return () => { cancelled = true }
  }, [erp, doctype])

  /** Every field somebody could sensibly put in a column. */
  const choosable = useMemo<DocField[]>(
    () => (meta?.fields ?? []).filter((f) =>
      !f.hidden && !['Section Break', 'Column Break', 'Tab Break', 'Heading', 'HTML',
                     'Table', 'Table MultiSelect', 'Button', 'Signature', 'Geolocation',
                     'Text Editor', 'Code', 'HTML Editor'].includes(f.fieldtype)),
    [meta])

  const columns = useMemo<DocField[]>(() => {
    // `meta.fields` is empty on a DocType this person may not read, so
    // everything below has to survive that rather than assume a form.
    if (!meta?.fields?.length) return []
    if (chosenColumns) {
      const by = new Map(choosable.map((f) => [f.fieldname, f]))
      const kept = chosenColumns.map((n) => by.get(n)).filter(Boolean) as DocField[]
      if (kept.length) return kept
    }
    const declared = meta.fields.filter((f) => f.in_list_view && !f.hidden)
    if (declared.length) return declared.slice(0, 6)
    // Nothing declared: show the title and whatever reads like a status.
    const fallback = meta.fields.filter((f) =>
      ['Data', 'Select', 'Link', 'Date', 'Currency'].includes(f.fieldtype) && !f.hidden)
    return fallback.slice(0, 5)
  }, [meta, chosenColumns, choosable])

  /** The DocType's own quick filters. Each one is drawn as what it *is*:
   *  rendering a Link as a dropdown produced "All Account Number" with the
   *  target doctype as its only option, which is a filter nobody can use. */
  const standardFilters = useMemo<DocField[]>(
    () => (meta?.fields ?? []).filter((f) => f.in_standard_filter && !f.hidden).slice(0, 3),
    [meta],
  )

  const groupable = useMemo(
    () => (meta?.fields ?? []).filter((f) => f.fieldtype === 'Select' || (f.fieldtype === 'Link' && f.in_standard_filter)).slice(0, 6),
    [meta],
  )

  const filters = useMemo(() => {
    const out: unknown[] = []
    for (const [field, value] of Object.entries(quick)) if (value) out.push([field, '=', value])
    if (search.trim()) out.push(['name', 'like', `%${search.trim()}%`])
    return out
  }, [quick, search])

  const board = useMemo(() => boardField(meta), [meta])
  const calendar = useMemo(() => calendarField(meta), [meta])
  // Gantt: the DocType's own `*_calendar.js`, harvested into the bundle.
  // Map: Frappe's own metadata rule, applied to the metadata we already have.
  const gantt = useMemo(() => ganttConfig(doctype), [doctype])
  const mappable = useMemo(() => canMap(meta), [meta])

  const fetch = useCallback(async (from: number, append: boolean) => {
    if (!meta) return
    const ticket = ++live.current
    setLoading(true); setError(null)
    try {
      const answer = await erp.list(doctype, {
        fields: ['name', 'modified', 'docstatus', ...columns.map((c) => c.fieldname),
                 ...(groupField ? [groupField] : []),
                 ...(board ? [board.fieldname] : []),
                 ...(calendar ? [calendar.fieldname] : []),
                 // A Gantt and a Map read fields the table does not show. Ask
                 // for them whenever the view is on offer, or switching to it
                 // draws an empty chart over rows that do have the data.
                 ...(gantt ? ganttFields(gantt) : []),
                 ...(mappable ? mapFields(mappable) : []),
                 ...(meta.title_field ? [meta.title_field] : [])],
        filters, orderBy: sort, start: from, limit: PAGE, withCount: from === 0,
      })
      if (ticket !== live.current) return   // a newer request has already answered
      setRows((current) => (append ? [...current, ...answer.rows] : answer.rows))
      readAt.current = Date.now()
      setStaleSince(null)
      setHasMore(answer.has_more)
      if (from === 0) setTotal(answer.total)
      setStart(from)
    } catch (e) {
      if (ticket === live.current) setError(e as ErpError)
    } finally {
      if (ticket === live.current) setLoading(false)
    }
  }, [erp, doctype, meta, columns, filters, sort, groupField, board, calendar, gantt, mappable])

  useEffect(() => {
    if (!meta || meta.no_access) { if (meta?.no_access) setLoading(false); return }
    if (firstPage.current) { firstPage.current = false; return }
    const timer = window.setTimeout(() => void fetch(0, false), search ? 220 : 0)
    return () => window.clearTimeout(timer)
  }, [meta, fetch, search])

  // A list left open on a second screen is a list that has quietly stopped
  // being true. Coming back to the tab is when somebody would notice, so
  // that is when it says so rather than silently re-fetching underneath them.
  useEffect(() => {
    const onBack = () => {
      if (document.visibilityState !== 'visible') return
      const minutes = Math.floor((Date.now() - readAt.current) / 60000)
      if (minutes >= STALE_AFTER_MINUTES) setStaleSince(`${minutes} minutes ago`)
    }
    document.addEventListener('visibilitychange', onBack)
    return () => document.removeEventListener('visibilitychange', onBack)
  }, [])

  useEffect(() => {
    if (!groupField || !meta) { setCounts([]); return }
    const controller = new AbortController()
    erp.groupCounts(doctype, groupField, filters, controller.signal)
      .then((r) => setCounts(r.counts ?? []))
      .catch(() => setCounts([]))
    return () => controller.abort()
  }, [erp, doctype, groupField, filters, meta])

  /**
   * Rows, grouped by the chosen column when there is one.
   *
   * `key: null` is "no grouping" — one bucket, drawn with no header. Grouping
   * only what is on this page is deliberate and said so on the totals row: a
   * subtotal that silently covered rows nobody can see would be a number to
   * act on that does not match the list under it.
   */
  const grouped = useMemo(() => {
    if (!groupField) return [{ key: null as string | null, rows }]
    const buckets = new Map<string, Doc[]>()
    for (const row of rows) {
      const key = String(row[groupField] ?? '')
      const list = buckets.get(key) ?? []
      list.push(row)
      buckets.set(key, list)
    }
    return [...buckets.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([key, list]) => ({ key, rows: list }))
  }, [rows, groupField])

  const allPicked = rows.length > 0 && rows.every((r) => picked.has(String(r.name)))
  const toggleAll = () =>
    setPicked(allPicked ? new Set() : new Set(rows.map((r) => String(r.name))))
  const toggleOne = (name: string) =>
    setPicked((current) => {
      const next = new Set(current)
      next.has(name) ? next.delete(name) : next.add(name)
      return next
    })

  /** Run a bulk action and say plainly what happened to each document. */
  const runBulk = async (action: 'submit' | 'cancel' | 'delete', extra?: Record<string, unknown>) => {
    const names = [...picked]
    if (names.length === 0) return
    setBusy(true)
    try {
      const answer = await erp.bulk(doctype, action, names, extra)
      if (answer.failed.length === 0) {
        toast('ok', `${answer.succeeded} ${answer.succeeded === 1 ? 'document' : 'documents'} ${action}ed.`)
      } else {
        // Partial success is the normal case, so it gets a real sentence
        // rather than a red box that hides the ones that worked.
        toast('error', `${answer.succeeded} of ${answer.total} ${action}ed.`,
              `${answer.failed[0]?.name}: ${answer.failed[0]?.why}` +
              (answer.failed.length > 1 ? ` (and ${answer.failed.length - 1} more)` : ''))
      }
      setPicked(new Set())
      await fetch(0, false)
    } catch (e) {
      toast('error', (e as ErpError).message)
    } finally {
      setBusy(false)
    }
  }

  const download = async (kind: 'csv' | 'xlsx') => {
    if (!meta) return
    setDownloading(kind); setError(null)
    try {
      await erp.downloadList(doctype, {
        fields: ['name', 'modified', 'docstatus', ...columns.map((c) => c.fieldname),
                 ...(meta.title_field ? [meta.title_field] : [])],
        filters, orderBy: sort,
      }, kind)
    } catch (e) {
      setError(e as ErpError)
    } finally {
      setDownloading(null)
    }
  }

  if (meta?.no_access) return <div className="page"><NoAccess what={meta.label ?? doctype} needs={meta.no_access.needs} /></div>
  if (error && rows.length === 0) return <div className="page"><Failed message={error.message} requestId={error.requestId} onRetry={() => fetch(0, false)} /></div>

  const filtering = Boolean(search) || Object.values(quick).some(Boolean)

  return (
    <div className="page" style={{ display: 'grid', gap: 14 }}>
      <div className="row-between" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1>{title ?? meta?.label ?? doctype}</h1>
          <div className="small muted">
            {total !== undefined ? `${total.toLocaleString('en-IN')} record${total === 1 ? '' : 's'}` : ' '}
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          {actions}
          {meta?.can.export !== false && rows.length > 0 ? (
            <div className="row" style={{ gap: 0 }}>
              <button className="btn btn-sm" disabled={downloading !== null}
                      style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                      onClick={() => void download('csv')}>
                {downloading === 'csv' ? 'Preparing…' : 'Download'}
              </button>
              <button className="btn btn-sm" aria-label="Download as Excel" disabled={downloading !== null}
                      style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderLeft: 0, padding: '0 8px' }}
                      onClick={() => void download('xlsx')}>xlsx</button>
            </div>
          ) : null}
          {meta?.allow_import && meta.can.create && onImport ? (
            <button className="btn btn-sm" onClick={onImport}>Import</button>
          ) : null}
          {meta?.can.create && onNew ? (
            <button className="btn btn-primary" onClick={onNew}>New</button>
          ) : null}
        </div>
      </div>

      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <input ref={searchBox} className="input" style={{ maxWidth: 280 }}
               placeholder={`Search ${meta?.label ?? ''}…`}
               value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search" />
        {standardFilters.map((f) => (
          <QuickFilter key={f.fieldname} field={f} value={quick[f.fieldname] ?? ''}
                       onChange={(v) => setQuick((q) => ({ ...q, [f.fieldname]: v }))} />
        ))}
        {groupable.length ? (
          <select className="select" style={{ maxWidth: 190 }} aria-label="Group by"
                  value={groupField ?? ''} onChange={(e) => setGroupField(e.target.value || null)}>
            <option value="">Group by…</option>
            {groupable.map((f) => <option key={f.fieldname} value={f.fieldname}>{f.label || f.fieldname}</option>)}
          </select>
        ) : null}
        {filtering ? (
          <button className="btn btn-quiet btn-sm" onClick={() => { setSearch(''); setQuick({}) }}>Clear</button>
        ) : null}
        <span className="grow" />
        {views.length ? (
          <select className="select" style={{ maxWidth: 170 }} aria-label="Saved views" value=""
                  onChange={(e) => {
                    const v = views.find((x) => x.name === e.target.value)
                    if (!v) return
                    setQuick(Object.fromEntries((v.filters as [string, string, string][])
                      .filter((f) => f[1] === '=').map((f) => [f[0], String(f[2])])))
                    setSearch(v.search ?? '')
                    if (v.columns) { setChosenColumns(v.columns); writeColumns(doctype, v.columns) }
                    if (v.orderBy) setSort(v.orderBy)
                  }}>
            <option value="">Saved views…</option>
            {views.map((v) => <option key={v.name} value={v.name}>{v.name}</option>)}
          </select>
        ) : null}
        <button className="btn btn-quiet btn-sm" onClick={() => {
          const name = window.prompt('Call this view what?')?.trim()
          if (!name) return
          setViews(saveView(doctype, {
            name, filters, search, orderBy: sort,
            columns: columns.map((c) => c.fieldname),
          }))
          toast('ok', `Saved “${name}”.`)
        }}>Save view</button>
        {columns.some(numeric) ? (
          <button className="btn btn-quiet btn-sm" aria-pressed={totalling}
                  style={totalling ? { background: 'var(--accent-soft)', color: 'var(--accent)' } : undefined}
                  onClick={() => setTotalling((v) => !v)}>Totals</button>
        ) : null}
        {(board || calendar || gantt || mappable) ? (
          <select className="select" style={{ maxWidth: 150 }} aria-label="How to show it"
                  value={shape} onChange={(e) => setShape(e.target.value as typeof shape)}>
            <option value="table">Table</option>
            {board ? <option value="board">Board</option> : null}
            {calendar ? <option value="calendar">Calendar</option> : null}
            {gantt ? <option value="gantt">Timeline</option> : null}
            {mappable ? <option value="map">Map</option> : null}
          </select>
        ) : null}
        <ColumnPicker fields={choosable} chosen={columns.map((c) => c.fieldname)}
                      onChange={(next) => { setChosenColumns(next); writeColumns(doctype, next) }} />
      </div>

      {picked.size > 0 ? (
        <div className="card card-pad row" role="region" aria-label="What to do with the selection"
             style={{ gap: 10, flexWrap: 'wrap', borderColor: 'var(--accent-line)', background: 'var(--accent-soft)' }}>
          <strong className="small">{picked.size} selected</strong>
          <span className="grow" />
          {meta?.is_submittable && meta.can.submit ? (
            <button className="btn btn-sm" disabled={busy} onClick={() => void runBulk('submit')}>Submit</button>
          ) : null}
          {meta?.is_submittable && meta.can.cancel ? (
            <button className="btn btn-sm" disabled={busy} onClick={() => void runBulk('cancel')}>Cancel</button>
          ) : null}
          {meta?.can.print ? (
            <button className="btn btn-sm" disabled={busy} onClick={async () => {
              setBusy(true)
              try { await erp.downloadManyPrints(doctype, [...picked]) }
              catch (e) { toast('error', (e as ErpError).message) }
              finally { setBusy(false) }
            }}>Print</button>
          ) : null}
          {meta?.can.delete ? (
            <button className="btn btn-sm btn-danger" disabled={busy}
                    onClick={() => {
                      if (window.confirm(`Delete ${picked.size} ${picked.size === 1 ? 'document' : 'documents'}? This cannot be undone.`))
                        void runBulk('delete')
                    }}>Delete</button>
          ) : null}
          <button className="btn btn-quiet btn-sm" onClick={() => setPicked(new Set())}>Clear</button>
        </div>
      ) : null}

      <div style={{ display: 'grid', gap: 14, gridTemplateColumns: counts.length ? 'minmax(0,1fr) 210px' : '1fr' }}
           className="erp-list-cols">
        <div className="card" style={{ overflow: 'hidden' }}>
          {/* Two states that only a real tenant's data reaches: a list too
              big to read, and one that has been sitting open too long. */}
          {total !== undefined && total > TOO_MANY ? (
            <TooMuchData count={total} onNarrow={() => searchBox.current?.focus()} />
          ) : null}
          {staleSince ? <Stale since={staleSince} /> : null}
          {loading && rows.length === 0 ? (
            <div style={{ padding: 16 }}><Loading rows={8} label={`Loading ${doctype}`} /></div>
          ) : rows.length === 0 ? (
            filtering
              ? <EmptyByFilter onClear={() => { setSearch(''); setQuick({}) }} />
              : <EmptyFirstTime what={(meta?.label ?? doctype).toLowerCase() + 's'}
                                action={meta?.can.create && onNew ? <button className="btn btn-primary" onClick={onNew}>Create the first one</button> : undefined} />
          ) : (
            shape === 'board' && board ? (
              <div style={{ padding: 12 }}>
                <KanbanView meta={meta!} rows={rows} field={board} onOpen={onOpen} />
              </div>
            ) : shape === 'calendar' && calendar ? (
              <div style={{ padding: 12 }}>
                <CalendarView meta={meta!} rows={rows} field={calendar} onOpen={onOpen} />
              </div>
            ) : shape === 'gantt' && gantt ? (
              <div style={{ padding: 12 }}>
                <GanttView rows={rows} config={gantt} onOpen={onOpen} title={title} />
              </div>
            ) : shape === 'map' && mappable ? (
              <div style={{ padding: 12 }}>
                <MapView rows={rows} kind={mappable} meta={meta} onOpen={onOpen} />
              </div>
            ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table className="tbl">
                  <thead>
                    <tr>
                      <th style={{ width: 34 }}>
                        <label className="check" style={{ minHeight: 0 }}>
                          <input type="checkbox" checked={allPicked} onChange={toggleAll}
                                 aria-label={allPicked ? 'Clear selection' : 'Select everything on this page'} />
                        </label>
                      </th>
                      <th>{meta?.title_field ? 'Name' : 'ID'}</th>
                      {columns.map((c) => (
                        <th key={c.fieldname} className={numeric(c) ? 'num' : undefined}>{c.label || c.fieldname}</th>
                      ))}
                      {meta?.is_submittable ? <th>Status</th> : null}
                      <th className="num">Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grouped.map(({ key, rows: inGroup }) => (
                      <Fragment key={key ?? '—'}>
                        {key !== null ? (
                          <tr style={{ cursor: 'default', background: 'var(--sunk)' }}>
                            <td colSpan={2} style={{ fontWeight: 650 }}>{key || 'Not set'}</td>
                            {columns.map((c) => (
                              <td key={c.fieldname} className={numeric(c) ? 'num' : undefined}
                                  style={{ fontWeight: 600 }}>
                                {numeric(c) ? sumText(inGroup, c) : ''}
                              </td>
                            ))}
                            {meta?.is_submittable ? <td /> : null}
                            <td className="num small muted">{inGroup.length}</td>
                          </tr>
                        ) : null}
                        {inGroup.map((row) => (
                      <tr key={String(row.name)} onClick={() => onOpen(String(row.name))}
                          tabIndex={0} role="link"
                          aria-selected={picked.has(String(row.name))}
                          onKeyDown={(e) => { if (e.key === 'Enter') onOpen(String(row.name)) }}>
                        <td onClick={(e) => e.stopPropagation()}>
                          <label className="check" style={{ minHeight: 0 }}>
                            <input type="checkbox" checked={picked.has(String(row.name))}
                                   onChange={() => toggleOne(String(row.name))}
                                   aria-label={`Select ${row.name}`} />
                          </label>
                        </td>
                        <td className="truncate" style={{ maxWidth: 280, fontWeight: 550 }}>
                          {String((meta?.title_field && row[meta.title_field]) || row.name)}
                        </td>
                        {columns.map((c) => (
                          <td key={c.fieldname} className={numeric(c) ? 'num' : 'truncate'} style={{ maxWidth: 240 }}>
                            {cellText(row, c)}
                          </td>
                        ))}
                        {meta?.is_submittable ? (
                          <td><span className={`chip ${docstatusChip(row.docstatus)}`}>
                            {DOCSTATUS_LABEL[(row.docstatus ?? 0) as 0 | 1 | 2]}
                          </span></td>
                        ) : null}
                        <td className="num small muted" title={String(row.modified ?? '')}>{formatSince(row.modified)}</td>
                      </tr>
                        ))}
                      </Fragment>
                    ))}
                  </tbody>
                  {totalling ? (
                    <tfoot>
                      <tr style={{ cursor: 'default', background: 'var(--sunk)' }}>
                        <td colSpan={2} style={{ fontWeight: 650 }}>
                          Total{rows.length < (total ?? rows.length) ? ' (this page)' : ''}
                        </td>
                        {columns.map((c) => (
                          <td key={c.fieldname} className={numeric(c) ? 'num' : undefined}
                              style={{ fontWeight: 650 }}>
                            {numeric(c) ? sumText(rows, c) : ''}
                          </td>
                        ))}
                        {meta?.is_submittable ? <td /> : null}
                        <td className="num small muted">{rows.length}</td>
                      </tr>
                    </tfoot>
                  ) : null}
                </table>
              </div>
              {hasMore ? (
                <div style={{ padding: 10, borderTop: '1px solid var(--line)', textAlign: 'center' }}>
                  <button className="btn" onClick={() => fetch(start + PAGE, true)} disabled={loading}>
                    {loading ? 'Loading…' : 'Show more'}
                  </button>
                </div>
              ) : null}
            </>
            )
          )}
        </div>

        {counts.length ? (
          <aside className="card card-pad" aria-label="Counts">
            <div className="small muted" style={{ marginBottom: 8 }}>
              By {groupable.find((g) => g.fieldname === groupField)?.label ?? groupField}
            </div>
            <div className="stack" style={{ gap: 2 }}>
              {counts.map((c) => (
                <button key={c.name ?? 'none'} className="navitem"
                        aria-current={quick[groupField!] === c.name ? 'page' : undefined}
                        onClick={() => setQuick((q) => ({ ...q, [groupField!]: q[groupField!] === c.name ? '' : c.name }))}>
                  <span className="grow truncate">{c.name || 'Not set'}</span>
                  <span className="small muted num">{c.count}</span>
                </button>
              ))}
            </div>
          </aside>
        ) : null}
      </div>
    </div>
  )
}

function QuickFilter({ field, value, onChange }: { field: DocField; value: string; onChange: (v: string) => void }) {
  const erp = useErp()
  const label = field.label || field.fieldname
  const [options, setOptions] = useState<{ value: string; label?: string }[]>([])
  const isLink = field.fieldtype === 'Link'

  useEffect(() => {
    if (!isLink || !field.options) return
    const controller = new AbortController()
    // The first page of what it links to: a quick filter is a shortcut, not
    // a search, so ten choices is the right size for one.
    //
    // Fetched when the browser is next idle, never on mount. These fired
    // while the rows were still being drawn and cost 460ms of the list's
    // first paint on a mid-range phone — for a control nobody touches in the
    // first second. Deferring them changes nothing a person can see.
    const start = () => {
      erp.searchLink(field.options!, '', undefined, controller.signal)
        .then((r) => setOptions(r.results ?? []))
        .catch(() => setOptions([]))
    }
    // Typed as optional on purpose: the DOM lib swears it is always there,
    // and older Safari says otherwise.
    const idle = (window as { requestIdleCallback?: typeof window.requestIdleCallback })
      .requestIdleCallback
    const handle = idle ? idle(start, { timeout: 2000 }) : window.setTimeout(start, 300)
    return () => {
      controller.abort()
      if (idle) window.cancelIdleCallback(handle as number)
      else window.clearTimeout(handle as number)
    }
  }, [erp, isLink, field.options])

  if (field.fieldtype === 'Check') {
    return (
      <select className="select" style={{ maxWidth: 170 }} aria-label={label}
              value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Any {label}</option>
        <option value="1">Yes</option>
        <option value="0">No</option>
      </select>
    )
  }
  if (field.fieldtype === 'Date' || field.fieldtype === 'Datetime') {
    return <input className="input" style={{ maxWidth: 170 }} type="date" aria-label={label}
                  value={value} onChange={(e) => onChange(e.target.value)} />
  }
  const choices = isLink
    ? options.map((o) => ({ value: o.value, label: o.label || o.value }))
    : (field.options ?? '').split('\n').filter(Boolean).map((o) => ({ value: o, label: o }))
  return (
    <select className="select" style={{ maxWidth: 190 }} aria-label={label}
            value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">All {label}</option>
      {choices.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
    </select>
  )
}

/** Which columns the list shows. The DocType's own choice is the default and
 *  "Reset" returns to it — a picker with no way back is a trap. */
function ColumnPicker({ fields, chosen, onChange }: {
  fields: DocField[]; chosen: string[]; onChange: (next: string[] | null) => void
}) {
  const [open, setOpen] = useState(false)
  const has = new Set(chosen)
  return (
    <div style={{ position: 'relative' }}>
      <button className="btn btn-quiet btn-sm" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        Columns
      </button>
      {open ? (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 29 }} onClick={() => setOpen(false)} />
          <div className="card" style={{ position: 'absolute', right: 0, top: 'calc(100% + 4px)', zIndex: 30,
                                         width: 260, maxHeight: 320, overflowY: 'auto', padding: 8,
                                         boxShadow: 'var(--shadow-2)' }}>
            <div className="row-between" style={{ marginBottom: 6 }}>
              <strong className="small">Columns</strong>
              <button className="btn btn-quiet btn-sm" onClick={() => { onChange(null); setOpen(false) }}>Reset</button>
            </div>
            {fields.map((f) => (
              <label key={f.fieldname} className="check" style={{ width: '100%', minHeight: 30 }}>
                <input type="checkbox" checked={has.has(f.fieldname)}
                       onChange={() => {
                         const next = has.has(f.fieldname)
                           ? chosen.filter((c) => c !== f.fieldname)
                           : [...chosen, f.fieldname]
                         onChange(next.length ? next : null)
                       }} />
                <span className="small truncate">{f.label || f.fieldname}</span>
              </label>
            ))}
          </div>
        </>
      ) : null}
    </div>
  )
}

/** The sum of one numeric column, formatted the way its own field would be. */
function sumText(rows: Doc[], column: DocField): string {
  const total = rows.reduce((n, r) => n + (Number(r[column.fieldname]) || 0), 0)
  if (!total) return ''
  return cellText({ ...rows[0], [column.fieldname]: total }, column)
}

const numeric = (f: DocField) => ['Int', 'Float', 'Currency', 'Percent'].includes(f.fieldtype)

function cellText(row: Doc, field: DocField): string {
  const v = row[field.fieldname]
  if (v === null || v === undefined || v === '') return ''
  switch (field.fieldtype) {
    case 'Currency': return formatCurrency(v, String(row.currency ?? 'INR'), Number(field.precision) || 2)
    case 'Float': return formatNumber(v, Number(field.precision) || undefined)
    case 'Percent': return `${formatNumber(v, 2)}%`
    case 'Int': return formatNumber(v, 0)
    case 'Date': return formatDate(v)
    case 'Datetime': return formatDatetime(v)
    case 'Check': return v === 1 ? 'Yes' : 'No'
    default: return String(v)
  }
}
